import Phaser from 'phaser';

const PLAYER_RADIUS = 18;
const VELOCITY_SMOOTHING_FACTOR = 14;
const HP_BAR_WIDTH = 40;
const HP_BAR_HEIGHT = 5;

export interface PlayerStats {
  hp: number;
  maxHp: number;
  damage: number;
  speed: number;
  attackSpeed: number;
  regen: number;
  armor: number;
  critChance: number;
  magnetRadius: number;
  // Weapon levels
  fireballLevel: number;
  lightningLevel: number;
  orbitCount: number;
  spinAttackLevel: number;
  frostNovaLevel: number;
  arrowRainLevel: number;
  boomerangLevel: number;
  laserBeamLevel: number;
  poisonCloudLevel: number;
  // Passive bonuses
  lifeSteal: number;
  goldBonus: number;
  xpBonus: number;
  shieldCharges: number;
  thornsPercent: number;
  luckBonus: number;
  doubleStrikeChance: number;
  timeWarpBonus: number;
  // Special flags
  hasExtraLife: boolean;
  extraLifeUsed: boolean;
  bossKillerActive: boolean;
  shieldBashActive: boolean;
  timeSlowActive: boolean;
  nuclearBombActive: boolean;
  // Synergy bonuses
  fireDamageBonus: number;
  lightningChainBonus: number;
  orbitCountBonus: number;
  defenseBonus: number;
}

const DEFAULT_STATS: PlayerStats = {
  damage: 12,
  speed: 240,
  maxHp: 100,
  hp: 100,
  attackSpeed: 1,
  regen: 0,
  armor: 0,
  critChance: 0,
  magnetRadius: 50,
  fireballLevel: 0,
  lightningLevel: 0,
  orbitCount: 0,
  spinAttackLevel: 0,
  frostNovaLevel: 0,
  arrowRainLevel: 0,
  boomerangLevel: 0,
  laserBeamLevel: 0,
  poisonCloudLevel: 0,
  lifeSteal: 0,
  goldBonus: 1,
  xpBonus: 1,
  shieldCharges: 0,
  thornsPercent: 0,
  luckBonus: 0,
  doubleStrikeChance: 0,
  timeWarpBonus: 0,
  hasExtraLife: false,
  extraLifeUsed: false,
  bossKillerActive: false,
  shieldBashActive: false,
  timeSlowActive: false,
  nuclearBombActive: false,
  fireDamageBonus: 0,
  lightningChainBonus: 0,
  orbitCountBonus: 0,
  defenseBonus: 0,
};

export class Player extends Phaser.GameObjects.Container {
  private readonly inputVector = new Phaser.Math.Vector2();
  private readonly velocity = new Phaser.Math.Vector2();

  stats: PlayerStats;

  private gfx: Phaser.GameObjects.Arc;
  private hpBarBg: Phaser.GameObjects.Rectangle;
  private hpBar: Phaser.GameObjects.Rectangle;

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y);

    this.stats = { ...DEFAULT_STATS };

    this.gfx = scene.add.circle(0, 0, PLAYER_RADIUS, 0x2d7ff9);
    const gfx = this.gfx;
    const leftEye = scene.add.circle(-6, -4, 2, 0xffffff);
    const rightEye = scene.add.circle(6, -4, 2, 0xffffff);

    const mouth = scene.add.graphics();
    mouth.lineStyle(2, 0xffffff, 1);
    mouth.beginPath();
    mouth.arc(0, 3, 6, 0.25, Math.PI - 0.25, false);
    mouth.strokePath();

    const barY = PLAYER_RADIUS + 10;
    this.hpBarBg = scene.add
      .rectangle(0, barY, HP_BAR_WIDTH, HP_BAR_HEIGHT, 0x333333)
      .setOrigin(0.5, 0.5);
    this.hpBar = scene.add
      .rectangle(-HP_BAR_WIDTH / 2, barY, HP_BAR_WIDTH, HP_BAR_HEIGHT, 0x44ff44)
      .setOrigin(0, 0.5);

    this.add([gfx, leftEye, rightEye, mouth, this.hpBarBg, this.hpBar]);
    this.setSize(PLAYER_RADIUS * 2, PLAYER_RADIUS * 2);

    scene.add.existing(this);
  }

  setInput(x: number, y: number): void {
    this.inputVector.set(x, y);

    if (this.inputVector.lengthSq() > 1) {
      this.inputVector.normalize();
    }
  }

  /** Apply damage. Returns true when the player dies. */
  takeDamage(amount: number): boolean {
    const effective = Math.max(0, amount - this.stats.armor);
    this.stats.hp = Math.max(0, this.stats.hp - effective);
    this.updateHpBar();
    this.flashRed();
    return this.stats.hp <= 0;
  }

  get isDead(): boolean {
    return this.stats.hp <= 0;
  }

  update(deltaMs: number, worldWidth: number, worldHeight: number): void {
    const deltaSeconds = deltaMs / 1000;
    const targetVelocityX = this.inputVector.x * this.stats.speed;
    const targetVelocityY = this.inputVector.y * this.stats.speed;

    const blend = Math.min(1, deltaSeconds * VELOCITY_SMOOTHING_FACTOR);
    this.velocity.x = Phaser.Math.Linear(this.velocity.x, targetVelocityX, blend);
    this.velocity.y = Phaser.Math.Linear(this.velocity.y, targetVelocityY, blend);

    this.x += this.velocity.x * deltaSeconds;
    this.y += this.velocity.y * deltaSeconds;

    const min = PLAYER_RADIUS;
    this.x = Phaser.Math.Clamp(this.x, min, worldWidth - min);
    this.y = Phaser.Math.Clamp(this.y, min, worldHeight - min);

    if (this.stats.regen > 0) {
      this.stats.hp = Math.min(
        this.stats.maxHp,
        this.stats.hp + this.stats.regen * deltaSeconds,
      );
      this.updateHpBar();
    }
  }

  private updateHpBar(): void {
    const ratio = this.stats.hp / this.stats.maxHp;
    const targetWidth = HP_BAR_WIDTH * ratio;
    const r = Math.floor(255 * (1 - ratio));
    const g = Math.floor(255 * ratio);
    this.hpBar.setFillStyle(Phaser.Display.Color.GetColor(r, g, 0));

    // Smooth width transition
    this.scene.tweens.killTweensOf(this.hpBar);
    this.scene.tweens.add({
      targets: this.hpBar,
      width: targetWidth,
      duration: 150,
      ease: 'Linear',
    });
  }

  /** Brief red tint to signal damage taken. */
  private flashRed(): void {
    this.gfx.setFillStyle(0xff3333);
    this.scene.time.delayedCall(120, () => {
      if (this.active) this.gfx.setFillStyle(0x2d7ff9);
    });
  }
}
